class Cart {
    /**
     * @param {string} storageKey - LocalStorage key for cart persistence
     */
    constructor(storageKey = 'cart') {
        this.cart = [];
        this.STORAGE_KEY = storageKey;
        this.loadCartFromStorage();
    }

    /**
     * Saves cart to localStorage
     */
    saveCartToStorage() {
        try {
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.cart));
        } catch (error) {
            console.warn('Failed to save cart to localStorage:', error);
        }
    }

    /**
     * Loads cart from localStorage
     */
    loadCartFromStorage() {
        try {
            const storedCart = localStorage.getItem(this.STORAGE_KEY);
            if (storedCart) {
                this.cart = JSON.parse(storedCart);
                if (!Array.isArray(this.cart)) {
                    this.cart = [];
                }
            }
        } catch (error) {
            console.warn('Failed to load cart from localStorage:', error);
            this.cart = [];
        }
    }

    /**
     * Clears cart from localStorage
     */
    clearCartStorage() {
        try {
            localStorage.removeItem(this.STORAGE_KEY);
        } catch (error) {
            console.warn('Failed to clear cart from localStorage:', error);
        }
    }

    /**
     * Adds item to cart or increments quantity if exists
     * @param {Object} item - Item to add
     * @param {string} identifierKey - Key to identify items (default: 'id')
     * @returns {Array} Updated cart array
     */
    addToCart(item, identifierKey = 'id') {
        const existingIndex = this.cart.findIndex(cartItem => 
            cartItem[identifierKey] === item[identifierKey]
        );
        
        if (existingIndex >= 0) {
            this.cart[existingIndex].quantity += 1;
        } else {
            this.cart.push({
                ...item,
                quantity: 1
            });
        }
        this.saveCartToStorage();
        return this.cart;
    }

    /**
     * Removes item from cart by identifier
     * @param {string|number} identifier - Value to match against identifierKey
     * @param {string} identifierKey - Key to identify items (default: 'id')
     * @returns {Array} Updated cart array
     */
    removeFromCart(identifier, identifierKey = 'id') {
        this.cart = this.cart.filter(item => item[identifierKey] !== identifier);
        this.saveCartToStorage();
        return this.cart;
    }

    /**
     * Updates quantity of item in cart
     * @param {string|number} identifier - Value to match against identifierKey
     * @param {number} quantity - New quantity (removes if <= 0)
     * @param {string} identifierKey - Key to identify items (default: 'id')
     * @returns {Array} Updated cart array
     */
    updateCartQuantity(identifier, quantity, identifierKey = 'id') {
        const item = this.cart.find(item => item[identifierKey] === identifier);
        if (item) {
            if (quantity <= 0) {
                return this.removeFromCart(identifier, identifierKey);
            }
            item.quantity = quantity;
        }
        this.saveCartToStorage();
        return this.cart;
    }

    /**
     * @returns {Array} Current cart items
     */
    getCart() {
        return this.cart;
    }

    /**
     * Calculates total cart value
     * @param {string} priceKey - Property name for price (default: 'price')
     * @returns {number} Total cart value
     */
    getCartTotal(priceKey = 'price') {
        return this.cart.reduce((total, item) => {
            return total + (parseFloat(item[priceKey]) * item.quantity);
        }, 0);
    }

    /**
     * Clears all items from cart
     * @returns {Array} Empty cart array
     */
    clearCart() {
        this.cart = [];
        this.clearCartStorage();
        return this.cart;
    }

    /**
     * @returns {number} Total quantity of all items in cart
     */
    getTotalItems() {
        return this.cart.reduce((sum, item) => sum + item.quantity, 0);
    }
}

export default Cart;

